import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Search, X, LogOut } from 'lucide-react';
import SideBarMenu from './SideBarMenu';
import ThemeSwitcher from '../ThemeSwitcher';
import { LogoutAction } from '../../Redux/Actions/AuthActions';
import './Sidebar.css';

export default function SideBar({ isOpen, onClose }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const profile_state = useSelector((state) => state.userDetails);
    const userProfile = profile_state?.userProfile;
    const userName = userProfile?.name || 'Administrator';
    const userRole = userProfile?.roles?.[0]?.name || userProfile?.role || 'Super Admin';
    const initials = userName
        ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
        : 'AD';

    const handleLogout = (e) => {
        e.preventDefault();
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        dispatch(LogoutAction(navigate));
    };

    return (
        <aside className={`ddwoms-sidebar ${isOpen ? 'is-open' : 'is-closed'}`}>
            {/* Brand Header */}
            <div className="ddwoms-sidebar-header flex items-center justify-between">
                <NavLink to="/dashboard" className="ddwoms-brand">
                    <div className="ddwoms-brand-logo-icon">
                        <span>V</span>
                    </div>
                    <div>
                        <div className="ddwoms-brand-text">VocalForLocal</div>
                        <div className="ddwoms-brand-tag">Work Order Portal</div>
                    </div>
                </NavLink>

                {/* Mobile Close Button */}
                <button
                    type="button"
                    onClick={onClose}
                    className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors cursor-pointer border-0 bg-transparent"
                    aria-label="Close sidebar"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Quick Menu Search */}
            <div className="ddwoms-sidebar-search">
                <div className="search-wrapper">
                    <Search className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search menu..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button
                            className="search-clear-btn"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Scrollable Navigation Options */}
            <div className="ddwoms-nav-scroll">
                <SideBarMenu searchQuery={searchQuery} />
                <div className="mt-4 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                    <ThemeSwitcher />
                </div>
            </div>

            {/* Sticky User Profile & Logout */}
            <div className="ddwoms-sidebar-footer">
                <div className="footer-profile-avatar">
                    {initials}
                </div>
                <div className="footer-profile-info">
                    <span className="footer-profile-name" title={userName}>{userName}</span>
                    <span className="footer-profile-role">{userRole}</span>
                </div>
                <button
                    className="footer-logout-btn"
                    onClick={handleLogout}
                    title={isLoggingOut ? "Signing out..." : "Sign Out"}
                    disabled={isLoggingOut}
                    style={{ opacity: isLoggingOut ? 0.7 : 1, cursor: isLoggingOut ? 'not-allowed' : 'pointer' }}
                >
                    {isLoggingOut ? (
                        <div
                            className="w-3.5 h-3.5 border-2 rounded-full animate-spin"
                            style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }}
                        />
                    ) : (
                        <LogOut className="w-4 h-4" />
                    )}
                </button>
            </div>
        </aside>
    );
}
